<template>
  <div
    style="overflow-y: auto"
    id="call-records-view"
    class="page-table"
    v-loading="loadingData"
  >
    <el-dialog
      width="440px"
      :visible="modalVisible"
      :before-close="handleCloseModal"
      :close-on-click-modal="false"
    >
      <h2 class="form-section-header">Filter Settings</h2>

      <h3 class="black-text mb-5">Select Date Range</h3>
      <el-date-picker
        class="mb-10"
        style="margin-left: auto"
        v-model="historyDateRange"
        type="datetimerange"
        align="right"
        unlink-panels
        format="yyyy-MM-dd HH:mm"
        :clearable="false"
        range-separator="To"
        start-placeholder="Start date"
        end-placeholder="End date"
        :picker-options="datePickerOptions"
      >
      </el-date-picker>

      <el-form id="stats-advanced-modal" class="form-modal">
        <h3 class="black-text mb-10">Select Distribution</h3>
        <el-radio-group v-model="distribution">
          <el-radio-button label="hourly">Hourly</el-radio-button>
          <el-radio-button type="default" label="daily">Daily</el-radio-button>
          <el-radio-button label="monthly">Monthly</el-radio-button>
          <el-radio-button label="none">None</el-radio-button>
        </el-radio-group>

        <h3 class="black-text mb-0">
          Filter By
          <el-popover trigger="hover" width="300px">
            <i
              class="more-info-icon el-icon-question"
              slot="reference"
              placement="bottom-start"
            />
            <p class="black-text mb-0 mt-0">
              Filtering by Tag will only apply to Inbound Queue Calls
            </p>
          </el-popover>
        </h3>
        <el-select
          v-model="primaryFilter"
          placeholder="Select Filter"
          clearable
          style="margin-top: 0"
        >
          <template slot="prefix"> Filter By </template>
          <el-option value="queues" label="Queues" />
          <el-option value="users" label="Users" />
          <el-option value="tag" label="Tag" />
        </el-select>

        <div v-if="primaryFilter === 'queues'">
          <el-select
            v-model="filterQueues"
            value-key="id"
            placeholder="Select Queues"
            clearable
            multiple
            filterable
            style="margin-top: 0"
          >
            <el-option
              v-for="item in queueOptions"
              :key="item.id"
              :value="item.id"
              :label="item.name"
            />
          </el-select>
        </div>

        <el-select
          v-if="primaryFilter === 'users'"
          v-model="extNums"
          value-key="membername"
          placeholder="Select Users"
          remote
          filterable
          clearable
          multiple
          :remote-method="searchAgents"
        >
          <el-option
            v-for="item in extOptions"
            :key="getRandomKey()"
            :label="
              item.membername.replace(/_/g, ' ') +
              '- ' +
              item.memberdevice.split('-')[0]
            "
            :value="item"
          />
        </el-select>

        <el-input
          v-if="primaryFilter === 'tag'"
          placeholder="Enter Call Tag"
          v-model="tag"
        />
        <template slot="prefix"> Call Tag </template>

        <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
      </el-form>
    </el-dialog>

    <div id="users-stats-view" class="no-table-box page-header">
      <div class="flex justify-space-between mb-20">
        <h2 class="mb-0 mt-0 text-left">
          <v-svg sprite="receptionist" class="voxo-purple"></v-svg>
          Agent Activity
        </h2>
        <el-tag>
          <i class="el-icon-info" style="margin-right: 4px"></i>
          Total Queues: {{ total }}
        </el-tag>
      </div>

      <div class="flex align-center mb-20">
        <el-radio-group
          v-model="presetSelection"
          @change="doPreset"
          class="hidden-xs-only"
        >
          <el-radio-button type="default" label="yesterday"
            >Yesterday</el-radio-button
          >
          <el-radio-button type="default" label="last10"
            >Last 10 Days</el-radio-button
          >
          <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
        </el-radio-group>

        <el-tooltip placement="top">
          <template slot="content">
            {{ selectedDateRange }}
            <br />
            Dist: {{ distribution[0].toUpperCase() + distribution.slice(1) }}
          </template>
          <el-button @click="modalVisible = true" class="ml-10">
            Filter
          </el-button>
        </el-tooltip>
        <el-button
          @click="resetTable"
          v-if="advancedActive"
          style="background-color: #f25a6b; color: #fff"
        >
          Reset
        </el-button>
        <el-dropdown class="ml-10">
          <el-button>
            Export
            <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item @click.native="doExport">
              <v-svg class="success-text" sprite="list" />
              CSV
            </el-dropdown-item>
            <el-dropdown-item @click.native="openScheduleReportModal">
              <v-svg class="voxo-orange" sprite="clock" />
              Schedule Report
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <div
        style="height: calc(100vh - 19em); width: 100%"
        v-if="tableData.length"
      >
        <div
          v-for="(item, index) in tableData"
          :key="index"
          class="agent-activity-queue-section"
        >
          <div class="queue-name-header">
            <h3 class="mb-5">{{ item.queueName }}</h3>
          </div>
          <div class="user-stats-table card-base card-shadow--medium">
            <vxe-grid
              round
              border="inner"
              ref="xGrid"
              :data="item.agentStats"
              height="350"
              auto-resize
              empty-text="No Data"
              :scroll-y="{ enabled: true, gt: 10, oSize: 0 }"
              :columns="
                distribute ? [expandColumn, ...tableColumns] : tableColumns
              "
            />
          </div>
        </div>
      </div>
    </div>

    <schedule-report-modal
      :modalVisible="scheduleReportVisible"
      :filterSettings="filterSettings"
      @cancelOperation="scheduleReportVisible = false"
    />
  </div>
</template>

<script>
import feathersClient, { hostServer } from "@/feathers/feathers-client";
import moment from "moment-timezone";
import uniqBy from "lodash/uniqBy";
import { ExportToCsv } from "export-to-csv";
import ScheduleReportModal from "@/components/schedule-reports/ScheduleReportModal.vue";

export default {
  name: "AgentActivity",

  props: {
    queueOptions: { required: true },
  },

  components: { ScheduleReportModal },

  data() {
    return {
      scheduleReportVisible: false,
      queryParams: null,
      filterSettings: null,

      tableData: [],
      exportCSV: false,
      distribute: false,
      presetSelection: "last10",
      advancedActive: false,
      loadingData: false,
      modalVisible: false,
      queueIds: [],
      allQueueIds: [],
      filterQueues: [],
      noQueues: true,
      dateStart: null,
      dateEnd: null,
      primaryFilter: null,
      extNums: [],
      filterExts: [],
      extOptions: [],
      distribution: "none",
      total: 0,
      queueSelectAll: false,
      tag: "",
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment();
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment();
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment();
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment();
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
      expandColumn: {
        type: "expand",
        width: "25px",
        showOverflow: true,
        slots: {
          content: ({ row }) => {
            return [
              <div class="users-stats-distribution-table">
                <vxe-table
                  border="none"
                  show-overflow
                  highlight-hover-row
                  show-header={false}
                  height="200"
                  empty-text="No Data"
                  scroll-y={{ enabled: true, gt: 10, oSize: 0 }}
                  data={row.distributionStats}
                >
                  <vxe-table-column field="name" width="190"></vxe-table-column>
                  <vxe-table-column
                    field="inboundComplete"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalRefused"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalWaitTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="avgWaitTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="queueTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="avgQueueTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="outboundComplete"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="outboundTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="pauseTimeAccountReview"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="pauseTimeBreak"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="pauseTimeLunch"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="pauseTimeMeeting"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="pauseTimePersonal"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="pauseTimeOther"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalPauseTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="loggedInTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="workTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                </vxe-table>
              </div>,
            ];
          },
        },
      },
      tableColumns: [
        {
          field: "name",
          title: "Name",
          showOverflow: true,
          width: 190,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.name}</span>];
            },
          },
        },
        {
          field: "inboundComplete",
          name: "Inbound Complete",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.inboundComplete}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Inbound Complete">
                  <v-svg sprite="call-inbound" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalRefused",
          title: "Refused",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalRefused}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Refused">
                  <v-svg sprite="call-block" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalWaitTime",
          title: "Total Wait Time",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.totalWaitTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Wait Time">
                  <v-svg sprite="clock" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "avgWaitTime",
          title: "Average Wait Time",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.avgWaitTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip
                  placement="top"
                  content="Average Wait Time Per Call"
                >
                  <v-svg sprite="clock" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "queueTalkTime",
          title: "Queue Talk Time",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.queueTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Queue Talk Time">
                  <v-svg sprite="clock" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "avgQueueTalkTime",
          title: "Average Queue Talk Time",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.avgQueueTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip
                  placement="top"
                  content="Avg Queue Talk Time Per Call"
                >
                  <v-svg sprite="clock" class="voxo-pink"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "outboundComplete",
          title: "Outbound Complete",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.outboundComplete}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Outbound Complete">
                  <v-svg sprite="call-outbound" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "outboundTalkTime",
          title: "OutboundTalkTime",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.outboundTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Outbound Talk Time">
                  <v-svg sprite="clock" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalTalkTime",
          title: "Total Talk Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.totalTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Talk Time">
                  <v-svg sprite="clock" class="voxo-purple"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "pauseTimeAccountReview",
          title: "Pause Account Review",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [
                <span>{this.timeFormatter(row.pauseTimeAccountReview)}</span>,
              ];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Pause Time Account Review">
                  <v-svg sprite="account-review" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "pauseTimeBreak",
          title: "Pause Break",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.pauseTimeBreak)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Pause Time Break">
                  <v-svg sprite="break" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "pauseTimeLunch",
          title: "Pause Lunch",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.pauseTimeLunch)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Pause Time Lunch">
                  <v-svg sprite="lunch" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "pauseTimeMeeting",
          title: "Pause Meeting",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.pauseTimeMeeting)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Pause Time Meeting">
                  <v-svg sprite="meeting" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "pauseTimePersonal",
          title: "Pause Personal",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.pauseTimePersonal)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Pause Time Personal">
                  <v-svg sprite="personal" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "pauseTimeOther",
          title: "Pause Other",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.pauseTimeOther)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Pause Time Other">
                  <v-svg sprite="pause-2" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalPauseTime",
          title: "Pause Total",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.totalPauseTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Pause Time Total">
                  <v-svg sprite="clock" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "loggedInTime",
          title: "Logged In Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.loggedInTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Logged In Time">
                  <v-svg sprite="clock" class="voxo-blue"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "workTime",
          title: "Work Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.workTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Work Time">
                  <v-svg sprite="clock" class="voxo-aqua"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.dateEnd && this.dateStart) {
        const dateStart = moment(this.dateStart).format("M/D/YY HH:mm");
        const dateEnd = moment(this.dateEnd).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
  },

  watch: {
    selectedTenant() {
      this.loadingData = true;
    },

    queryParams(params) {
      if (params) {
        const {
          type,
          tenantId,
          tenantCode,
          primaryFilter,
          queueIds,
          agents,
          tag,
          distribution,
          timezone,
        } = params;

        const queryParams = {
          type,
          tenantId: {
            label: this.selectedTenant.name,
            value: tenantId,
          },
          tenantCode,
          primaryFilter: {
            label: "",
            value: "",
          },
          queueIds: [],
          agents,
          tag,
          distribution,
          timezone,
        };

        if (primaryFilter) {
          const primaryFilterMap = {
            queues: "Queues",
            tag: "Call Tag",
          };

          queryParams["primaryFilter"] = {
            label: primaryFilterMap[primaryFilter],
            value: primaryFilter,
          };
        }

        if (queueIds.length) {
          queryParams.queueIds = this.queueOptions
            .filter((queue) => queueIds.includes(queue.id))
            .map(({ id, name }) => ({ label: name, value: id }));
        }

        this.filterSettings = {
          servicePath: "account-statistics",
          query: queryParams,
        };
      }
    },
  },

  methods: {
    openScheduleReportModal() {
      this.scheduleReportVisible = true;
    },

    initData() {
      //default to first 10 queues in the list.
      if (this.queueOptions.length) {
        this.noQueues = false;
        this.allQueueIds = this.queueOptions.map((item) => item.id);
        this.queueIds = this.allQueueIds.slice(0, 10);
        this.doPreset("init");
      } else {
        if (this.chart) {
          this.chart.dispose();
        }
        this.noQueues = true;
        console.log("there are no queues for you to look at, sorry");
      }
    },
    updateDateRangePicker() {
      this.historyDateRange = [moment(this.dateStart), moment(this.dateEnd)];
    },
    doPreset(val) {
      if (val === "init") {
        this.dateEnd = moment().format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "last10") {
        this.dateEnd = moment().format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().subtract(10, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "lastMonth") {
        this.dateEnd = moment().format("YYYY-MM-DD HH:mm");
        this.dateStart = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "yesterday") {
        this.dateEnd = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.dateStart = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.updateDateRangePicker();
        this.queryStats();
      }
    },
    handleCloseModal() {
      this.modalVisible = false;
    },
    doAdvanced() {
      this.dateStart = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.dateEnd = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      //if hourly distribution make sure it passes 30 day limit
      let allowed = 1;
      if (this.distribution === "hourly") {
        allowed =
          moment(this.dateEnd).diff(
            moment(this.dateStart).add(1, "day"),
            "days"
          ) <= 2;
      }

      if (allowed) {
        this.updateDateRangePicker();
        this.queryStats();
        this.handleCloseModal();
        this.advancedActive = true;
        this.distribute = this.distribution !== "none";
        this.presetSelection = null;
      } else {
        this.$notify({
          type: "error",
          message: "Max of 2 days exceeded for hourly distribution",
          title: "Limit Exceeded",
        });
      }
    },
    resetTable(tenantChanged) {
      this.advancedActive = false;
      this.primaryFilter = "";
      this.filterQueues = [];
      this.filterExts = [];
      this.extNums = [];
      this.extOptions = [];
      this.exportCSV = false;
      this.distribution = "none";
      this.tag = "";
      this.distribute = false;
      if (tenantChanged) {
        this.initData();
      } else {
        this.doPreset("init");
      }
    },
    doExport() {
      this.$confirm(`Export data for ${this.total} queues?`, {
        type: "warning",
      })
        .then(() => {
          //export from browser
          let csvData = [];
          const exclude = [
            "loginTime",
            "logoutTime",
            "_XID",
            "extNumber",
            "inboundTransferred",
          ];
          for (let i = 0; i < this.tableData.length; i++) {
            const currentGroup = this.tableData[i];
            csvData.push({
              name: currentGroup.queueName,
              inboundComplete: "",
              totalRefused: "",
              totalWaitTime: "",
              avgWaitTime: "",
              queueTalkTime: "",
              avgQueueTalkTime: "",
              outboundComplete: "",
              outboundTalkTime: "",
              totalTalkTime: "",
              pauseTimeAccountReview: "",
              pauseTimeBreak: "",
              pauseTimeLunch: "",
              pauseTimeMeeting: "",
              pauseTimePersonal: "",
              pauseTimeOther: "",
              totalPauseTime: "",
              loggedInTime: "",
              workTime: "",
            });
            for (let j = 0; j < currentGroup.agentStats.length; j++) {
              const csvObject = {};
              const agentStats = currentGroup.agentStats[j];
              for (let key in agentStats) {
                if (!exclude.includes(key)) {
                  csvObject[key] =
                    agentStats[key] === null ? 0 : agentStats[key];
                }
              }
              csvData.push(csvObject);
              const distStats = agentStats.distributionStats;
              if (distStats) {
                for (let k = 0; k < distStats.length; k++) {
                  const csvObjectDist = {};
                  for (let key in distStats[k]) {
                    if (!exclude.includes(key)) {
                      csvObjectDist[key] =
                        distStats[k][key] === null ? 0 : distStats[k][key];
                    }
                  }
                  if (Object.keys(csvObjectDist).length)
                    csvData.push(csvObjectDist);
                }
              }
            }
          }

          const options = {
            filename: "queue_agent_activity",
            showLabels: true,
            headers: [
              "Name",
              "Inbound Complete",
              "Refused",
              "Wait Time",
              "Average Wait Time Per Call",
              "Queue Talk Time",
              "Average Queue Talk Time Per Call",
              "Outbound Complete",
              "Outbound Talk Time",
              "Total Talk Time",
              "Pause Time Account Review",
              "Pause Time Break",
              "Pause Time Lunch",
              "Pause Time Meeting",
              "Pause Time Personal",
              "Pause Time Other",
              "Pause Time Total",
              "Logged In Time",
              "Work Time",
            ],
          };

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(csvData);
        })
        .catch(() => true);
    },
    queryStats() {
      this.loadingData = true;

      //if users are the filter we need to rebuild the queueIds being sent out
      if (this.primaryFilter === "users") {
        this.filterExts = this.extNums.map((item) => item.membername);
        this.queueIds = this.allQueueIds;
      }

      this.queryParams = {
        type: "agentActivity",
        tenantId: this.selectedTenant.id,
        tenantCode: this.selectedTenant.tenantCode,
        primaryFilter: this.primaryFilter,
        queueIds:
          this.primaryFilter === "queues" ? this.filterQueues : this.queueIds,
        agents: this.filterExts,
        tag: this.tag,
        distribution: this.distribution,
        timezone: this.user.timezone,
      };

      feathersClient
        .service("account-statistics")
        .create({
          dateStart: this.dateStart,
          dateEnd: this.dateEnd,
          exportCSV: this.exportCSV,
          ...this.queryParams,
        })
        .then((res) => {
          if (!this.exportCSV) {
            this.total = res.total;
            this.tableData = res.data;
            this.pages = Math.ceil(res.total / this.limit);
            this.page = this.page > this.pages ? 1 : this.page;
          }

          this.exportCSV = false;
          this.loadingData = false;

          setTimeout(() => {
            const arrowDown = document.querySelectorAll(".vxe-sort--desc-btn");
            const arrowUp = document.querySelectorAll(".vxe-sort--asc-btn");
            const arrows = [...arrowDown, ...arrowUp];
            for (let i = 0; i < arrows.length; i++) {
              arrows[i].removeAttribute("title");
            }
          }, 1000);
        })
        .catch(() => {
          console.log("Could Not Process Stats Request");
          this.loadingData = false;
        });
    },
    async searchAgents(query) {
      if (query.length) {
        const searchTerm = (query + "")
          .replace(/[\\"']/g, "\\$&")
          .replace(/\u0000/g, "\\0");

        const allowedMembers = await feathersClient
          .service("queuemembers-allowed")
          .find({
            query: {
              queueName: {
                $in:
                  this.primaryFilter === "queues"
                    ? this.filterQueues
                    : this.allQueueIds,
              },
              $or: [
                { membername: { $like: `%${searchTerm}%` } },
                { memberdevice: { $like: `%${searchTerm}%` } },
              ],
              $limit: 25,
            },
          });

        const members = await feathersClient.service("queuemembers").find({
          query: {
            queueName: {
              $in:
                this.primaryFilter === "queues"
                  ? this.filterQueues
                  : this.allQueueIds,
            },
            $or: [
              { membername: { $like: `%${searchTerm}%` } },
              { memberdevice: { $like: `%${searchTerm}%` } },
            ],
            $limit: 25,
          },
        });

        const allMembers = [...allowedMembers, ...members];
        this.extOptions = uniqBy(allMembers, "membername");
      }
    },
    timeFormatter(value) {
      if (value < 0) {
        return "00:00:00";
      }
      if (value || value === 0) {
        return moment.duration(value, "seconds").format("HH:mm:ss", {
          trim: false,
        });
      } else {
        return "";
      }
    },
    timeFormatterTable(data) {
      if (data.cellValue < 0) {
        return this.timeFormatter(0);
      }
      return this.timeFormatter(data.cellValue);
    },
    waitFormat(data) {
      return data.cellValue ? data.cellValue : "-";
    },
    getRandomKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },
  },
};
</script>
